<script lang="ts" setup>
import { Delete } from "@element-plus/icons-vue";
import { useExpressionValidator } from "~/NewFormBuilder/composables/useExpressionValidator";
import {
  CustomExpressionFormElementEvent,
  FormDefinition,
  FormElementBase,
  FormElementEventBase,
  FormElementEventTrigger,
  FormElementEventType,
  FormElementType,
  PercentageToFormElementEvent,
  SumToFormElementEvent,
} from "~/api";
import { Slotted } from "~/types/Slotted";
import {
  formElementEventTriggerDisplayNames,
  formElementEventTypeDisplayNames,
} from "~/utils/enumDisplayNames";
import FormElementSelect from "../FormElementSelect.vue";
// import { evaluate, parse, SymbolNode, MathNode } from "mathjs";
import { onMounted, ref } from "vue";

const modelValue = defineModel<FormElementEventBase[]>({ required: true });

const props = defineProps<{
  /** Form element to edit events of. */
  formElement: FormElementBase;
  form: FormDefinition;
  /** Event types allowed to be used for this form element. */
  validEventTypes: FormElementEventType[];
}>();

const { validationResult, validateExpression } = useExpressionValidator();

const errorMessages = ref<Record<string, string>>({});

const getValidDependencyElements = (
  event: FormElementEventBase,
): FormElementBase[] => {
  let result: FormElementBase[] = props.form.components.filter(
    (el) => el.guid !== props.formElement.guid && !el.isArchived,
  );

  if (event.eventType === FormElementEventType.SUM_TO) {
    result = result.filter(
      (el) =>
        el.type === FormElementType.NUMBER ||
        el.type === FormElementType.MONEY ||
        el.type === FormElementType.RADIO_BUTTONS || // works if the values are numbers
        el.type === FormElementType.CHECKBOXES, // works if the values are numbers
    );
  } else if (event.eventType === FormElementEventType.PERCENTAGE_TO) {
    result = result.filter(
      (el) =>
        el.type === FormElementType.NUMBER ||
        el.type === FormElementType.MONEY ||
        el.type === FormElementType.RADIO_BUTTONS || // works if the values are numbers
        el.type === FormElementType.CHECKBOXES, // works if the values are numbers
    );
  }

  // map the results to add a the "Repeater" question code as a prefix if the element is inside a repeater
  result = result.map((el) => {
    const questionCode = el.questionCode;
    const repeaterQuestionCode = props.form.components.find(
      (c) => c.type === FormElementType.REPEATER && c.guid === el.parentGuid,
    )?.questionCode;
    if (repeaterQuestionCode) {
      return {
        ...el,
        questionCode: `${repeaterQuestionCode}: ${questionCode}`,
      };
    }
    return el;
  });

  return result;
};

const handleAdd = () => {
  const toAdd: FormElementEventBase = {
    // THIS MUST BE AT THE TOP
    eventType: props.validEventTypes[0],
    guid: undefined,
    trigger: FormElementEventTrigger.ON_SAVE,
    formElementGuid: props.formElement.guid,
  };
  modelValue.value.push(toAdd);
};

const handleRemove = (val: FormElementEventBase) => {
  const i = modelValue.value.indexOf(val);
  if (i !== -1) {
    modelValue.value.splice(i, 1);
  }
};

/**
 * type guard to check if a FormElementEventBase is a CustomExpressionFormElementEvent
 */
function isCustomExpression(
  val: FormElementEventBase,
): val is CustomExpressionFormElementEvent {
  return val.eventType === FormElementEventType.CUSTOM_EXPRESSION;
}

/**
 * this function is used to get the possible question codes from a custom expression
 * it uses a regex to match all possible question codes in the expression
 * @returns an array of question codes
 * @param customExpression the CustomExpressionFormElementEvent to get the question codes from
 */
const getPossibleQuestionCodes = (
  customExpression: CustomExpressionFormElementEvent,
) => {
  const regex = /"[^"]*"|\b(\w+)\b/g;
  const matches = customExpression.customExpression.match(regex);

  if (!matches) {
    errorMessages.value[customExpression.customExpression] =
      "Invalid expression";
    return [];
  }

  const questionCodeArray = matches.map((match) => {
    return match.replace(/(^"|"$)/g, "");
  });

  return questionCodeArray;
};

const validateCustomExpression = async (
  row: CustomExpressionFormElementEvent,
) => {
  const exp = row.customExpression;

  if (!exp) {
    errorMessages.value[row.customExpression] = "";
    return;
  }

  const questionCodes = getPossibleQuestionCodes(row);

  try {
    await validateExpression(exp, questionCodes, props.form.components);

    if (validationResult.value?.isValid) {
      errorMessages.value[row.customExpression] = "";
    } else {
      errorMessages.value[row.customExpression] =
        validationResult.value?.message || "Invalid expression";
    }
  } catch (e) {
    errorMessages.value[row.customExpression] = "Error during validation";
    console.log("SignalR expression validation failed", e);
  }
};

onMounted(() => {
  modelValue.value.forEach((row) => {
    if (isCustomExpression(row)) {
      validateCustomExpression(row as CustomExpressionFormElementEvent);
    }
  });
});
</script>

<template>
  <div class="elementEventsEditor">
    <el-table :data="modelValue" border>
      <el-table-column label="Event">
        <template #default="{ row }: Slotted<FormElementEventBase>">
          <el-select v-model="row.eventType">
            <el-option
              v-for="[value, label] in Object.entries(
                formElementEventTypeDisplayNames,
              ).filter(([value]) =>
                validEventTypes.includes(value as FormElementEventType),
              )"
              :key="value"
              :label="label"
              :value="value"
            />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column label="Computation" width="300">
        <template #default="{ row }: Slotted<FormElementEventBase>">
          <template v-if="row.eventType === FormElementEventType.SUM_TO">
            <FormElementSelect
              v-model="(row as SumToFormElementEvent).formElementGuidsToSum"
              multiple
              :options="getValidDependencyElements(row)"
            />
          </template>
          <div v-else-if="row.eventType === FormElementEventType.PERCENTAGE_TO">
            <FormElementSelect
              v-model="
                (row as PercentageToFormElementEvent).numeratorFormElementGuid
              "
              :options="getValidDependencyElements(row)"
            />
            <hr />

            <FormElementSelect
              v-model="
                (row as PercentageToFormElementEvent).denominatorFormElementGuid
              "
              :options="getValidDependencyElements(row)"
            />
          </div>
          <div v-else-if="isCustomExpression(row)">
            <el-input
              v-model="
                (row as CustomExpressionFormElementEvent).customExpression
              "
              @input="
                () =>
                  validateCustomExpression(
                    row as CustomExpressionFormElementEvent,
                  )
              "
            />
            <el-text
              v-if="errorMessages[row.customExpression]"
              type="danger"
              size="small"
            >
              {{ errorMessages[row.customExpression] }}
            </el-text>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Trigger">
        <template #default="{ row }: Slotted<FormElementEventBase>">
          <el-select v-model="row.trigger">
            <el-option
              v-for="[value, label] in Object.entries(
                formElementEventTriggerDisplayNames,
              )"
              :key="value"
              :label="label"
              :value="value"
            />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column align="center" width="65">
        <template #default="{ row }: Slotted<FormElementEventBase>">
          <el-button
            :icon="Delete"
            tabindex="-1"
            size="small"
            @click="() => handleRemove(row)"
          />
        </template>
      </el-table-column>
    </el-table>

    <el-button
      v-if="modelValue.length === 0"
      class="addEvent"
      style="width: 100%"
      :disabled="validEventTypes.length === 0 || !!formElement.isArchived"
      @click="handleAdd"
    >
      Add
    </el-button>
  </div>
</template>

<style lang="less" scoped>
.elementEventsEditor {
  margin: 8px 0;

  .addEvent.el-button {
    border-radius: 0px;
  }
}
</style>
